/** Read a non-HttpOnly cookie (e.g. csrf_token) from document.cookie. */
export function readBrowserCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const matches = [
    ...document.cookie.matchAll(new RegExp(`(?:^|; )${escaped}=([^;]*)`, 'g')),
  ]
  if (matches.length === 0) return null
  // Prefer the last duplicate (aligns with Starlette / common browser send order).
  const raw = matches[matches.length - 1]?.[1]
  return raw ? decodeURIComponent(raw) : null
}

/** Double-submit CSRF header from the readable `csrf_token` cookie. */
export function csrfHeaders(): Record<string, string> {
  const csrf = readBrowserCookie('csrf_token')
  return csrf ? { 'X-CSRF-Token': csrf } : {}
}

/** True when a 403 body is the backend CSRF double-submit failure. */
export function isCsrfFailureDetail(detail: unknown): boolean {
  if (typeof detail !== 'string') return false
  return detail.toLowerCase().includes('csrf token validation failed')
}

let csrfEnsureInFlight: Promise<string | null> | null = null

/**
 * Ensure a readable `csrf_token` cookie exists (issues one via GET if missing).
 * Safe to call on app boot and before mutating requests.
 * Pass `force: true` after a CSRF 403 to rotate the cookie.
 */
export async function ensureCsrfCookie(
  options: { force?: boolean } = {},
): Promise<string | null> {
  if (!options.force) {
    const existing = readBrowserCookie('csrf_token')
    if (existing) return existing
  }

  if (csrfEnsureInFlight) return csrfEnsureInFlight

  csrfEnsureInFlight = (async () => {
    try {
      const response = await fetch('/api/v1/csrf-token/', {
        method: 'GET',
        credentials: 'include',
      })
      if (!response.ok) return null
      // Prefer the JSON body: it is the token just Set-Cookie'd and avoids
      // reading a stale duplicate host-only csrf_token from document.cookie.
      const body = (await response.json().catch(() => null)) as {
        csrf_token?: string
      } | null
      if (body?.csrf_token) return body.csrf_token
      return readBrowserCookie('csrf_token')
    } catch {
      return null
    } finally {
      csrfEnsureInFlight = null
    }
  })()

  return csrfEnsureInFlight
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

/**
 * Build fetch init for cookie-session calls, attaching double-submit CSRF
 * on mutating methods so raw `fetch()` matches the axios interceptor.
 */
export async function csrfRequestInit(
  init: RequestInit = {},
): Promise<RequestInit> {
  const method = (init.method ?? 'GET').toUpperCase()
  const headers = new Headers(init.headers)
  if (!SAFE_METHODS.has(method)) {
    const token = await ensureCsrfCookie()
    if (token) {
      headers.set('X-CSRF-Token', token)
    }
  }
  return {
    ...init,
    credentials: init.credentials ?? 'include',
    headers,
  }
}
